import { useState, useEffect } from "react";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { formatNumber } from "@/lib/utils";
import { TweetCard } from "./TweetCard";
import { EmptyState } from "./EmptyState";
import { LoadingSpinner } from "./LoadingSpinner";
import type { Tweet } from "@/types/api";

interface TweetsListProps {
  tweets: Tweet[];
  loading: boolean;
  hasMore: boolean;
  total: number;
  filters: {
    type: 'all' | 'likes' | 'bookmarks';
  };
  loadMoreTweets: () => void;
}

export function TweetsList({ tweets, loading, hasMore, total, filters, loadMoreTweets }: TweetsListProps) {
  const sentinelRef = useInfiniteScroll({
    enabled: !loading && hasMore,
    onLoadMore: loadMoreTweets,
  });

  const [modalImage, setModalImage] = useState<string | null>(null);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setModalImage(null);
      }
    };

    if (modalImage) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [modalImage]);

  if (loading && tweets.length === 0) {
    return <LoadingSpinner />;
  }

  if (tweets.length === 0) {
    return <EmptyState />;
  }

  const typeNames = { all: "全部", likes: "点赞", bookmarks: "收藏" };

  return (
    <main className="main-content min-h-[400px]">
      <div className="tweets-header flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{typeNames[filters.type]}推文</h2>
        <span className="tweets-count text-text-muted text-xs">
          共 {formatNumber(total)} 条
        </span>
      </div>

      <div className="tweets-list flex flex-col gap-4">
        {tweets.map((tweet) => (
          <TweetCard
            key={tweet.rest_id}
            tweet={tweet}
            onImageClick={setModalImage}
          />
        ))}
      </div>

      <div
        ref={sentinelRef}
        className="scroll-sentinel h-px -mt-px pointer-events-none"
      />

      {loading && tweets.length > 0 && (
        <div className="loading-indicator flex flex-col items-center justify-center py-8 gap-4 text-text-secondary">
          <div className="spinner w-8 h-8 border-3 border-bg-tertiary border-t-accent rounded-full animate-spin" />
          <span>加载更多...</span>
        </div>
      )}

      {!hasMore && tweets.length > 0 && (
        <div className="end-of-list text-center py-8 text-text-muted text-sm">
          没有更多推文了
        </div>
      )}

      {/* Modal */}
      {modalImage && (
        <div
          className="modal fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-5"
          onClick={() => setModalImage(null)}
        >
          <div
            className="modal-content bg-bg-secondary rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto relative p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-text-secondary hover:bg-bg-tertiary transition-colors text-2xl"
              onClick={() => setModalImage(null)}
            >
              ×
            </button>
            <img
              src={modalImage}
              alt="媒体"
              className="max-w-full rounded-media"
            />
          </div>
        </div>
      )}
    </main>
  );
}
